import { ReactNode } from 'react';
import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    Box,
    Typography,
    Tooltip,
    Stack
} from '@mui/material';
import { QuizTypes } from 'consts';
import { useQuizContext, useQuizSessionContext } from 'contexts';
import { QuizSessionOverview_quiz_master_quiz_session_quiz_session_user_progresses as UserSessions } from 'graphql/subscriptions/types/QuizSessionOverview';
import { BattleshipQuizSetup, BattleshipUserSessionAdditionalInfo } from '../../battleship-quiz/types';
import { SharpshooterQuizSetup, SharpshooterUserSessionAdditionalInfo } from '../../sharpshooter/sharpshooter-types';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import { getDurationString, getFormattedDateTime, getPlayDuration } from './overview-tools';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

interface UserSessionDisplayData {
    id: number;
    userDisplayName: string;
    correctAnswers: number;
    incorrectAnswers: number;
    timeStarted: string;
    timeLastAnswer: string;
    playDuration: string;
    hasEndedSession?: boolean;
    additionalInfo?: BattleshipUserSessionAdditionalInfo | SharpshooterUserSessionAdditionalInfo;
}

type AdditionalInfoType = BattleshipUserSessionAdditionalInfo | SharpshooterUserSessionAdditionalInfo | undefined;

interface Props {
    userSessions: UserSessions[];
}

/**
 * Component gives the Admin an overview of the session:
 * * the quiz basic info
 * * the setup for the quiz
 * * the players and their scores in real time (using a subscription)
 * @param props
 * @returns
 */
export const BattleshipOverview = (props: Props) => {
    const sessionContext = useQuizSessionContext();
    const { quiz } = useQuizContext();

    const getIncorrectOrSkippedCount = (answerAttempts: number, correctCount: number): number => {
        return answerAttempts - correctCount;
    };

    const getSunkShipCount = (info: AdditionalInfoType): number => {
        return info?.shipStatus ? info.shipStatus.reduce<number>((sum, ts) => sum + ts.sunkShips, 0) : 0;
    };

    const getPreparedData = (userSessions: UserSessions[]): UserSessionDisplayData[] => {
        var sortedUserSessions = [...userSessions].sort((usa, usb) => {
            var aSunk = getSunkShipCount(usa.additionalInfo);
            var bSunk = getSunkShipCount(usb.additionalInfo);
            if (aSunk !== bSunk) {
                return bSunk - aSunk;
            }

            if (usa.timeStarted - usa.timeLastAnswer !== usb.timeStarted - usb.timeLastAnswer) {
                return usb.timeStarted - usb.timeLastAnswer - (usa.timeStarted - usa.timeLastAnswer);
            }

            return usb.correctAnswers - usa.correctAnswers;
        });

        var uss = sortedUserSessions.map((us) => ({
            id: us.id,
            userDisplayName: us.user.displayName,
            correctAnswers: us.correctAnswers,
            incorrectAnswers: getIncorrectOrSkippedCount(us.answersAttempted, us.correctAnswers),
            timeStarted: getFormattedDateTime(us.timeStarted),
            timeLastAnswer: getFormattedDateTime(us.timeLastAnswer),
            playDuration: getPlayDuration(us.timeStarted, us.timeLastAnswer),
            hasEndedSession: us.hasEnded,
            additionalInfo: us.additionalInfo
        }));

        return uss;
    };

    const userSessions = getPreparedData(props.userSessions);
    const quizSetup: BattleshipQuizSetup | SharpshooterQuizSetup = sessionContext?.session.quizSettings;

    const getSunkShipsStatus = (row: UserSessionDisplayData): ReactNode => {
        switch (quiz.quizTypeId) {
            case QuizTypes.Battleship:
                return row.additionalInfo?.shipStatus?.at(0)?.sunkShips ?? 0;

            case QuizTypes.Sharpshooter:
                const quizSetup: SharpshooterQuizSetup = sessionContext?.session.quizSettings;
                if (quizSetup.teams.length === 1) {
                    return row.additionalInfo?.shipStatus?.at(0)?.sunkShips ?? 0;
                }
                return (
                    <>
                        {quizSetup.teams.map((t) => (
                            <p>{`${t.name}: ${row.additionalInfo?.shipStatus?.find((ss) => ss.teamName === t.name)?.sunkShips ?? 0}`}</p>
                        ))}
                    </>
                );

            default:
                break;
        }
    };

    const getShipCount = () => {
        switch (quiz.quizTypeId) {
            case QuizTypes.Battleship:
                const battleshipSetup: BattleshipQuizSetup = sessionContext?.session.quizSettings;
                return battleshipSetup.teams[0].shipLocations.length;

            case QuizTypes.Sharpshooter:
                const sharpshooterSetup: SharpshooterQuizSetup = sessionContext?.session.quizSettings;
                return sharpshooterSetup.teams[0].shipLocations.length;

            default:
                return null;
        }
    };

    return (
        <div className="aq-content">
            <Typography variant="h2" className="quiz-title">
                {quiz.name}
            </Typography>
            <br />
            <Box>
                <Paper sx={{ padding: 5 }}>
                    <Typography variant="h4" sx={{ marginBottom: 5 }}>
                        Quiz settings
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography textAlign="start">
                                <b>Type:</b> {QuizTypes[quiz.quizTypeId]}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography textAlign="start">
                                <b>Time limits</b>
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Stack spacing={1} direction="row">
                                <Typography textAlign="start">
                                    <b>Total:</b>
                                </Typography>
                                {sessionContext?.session.maxDurationSec ? (
                                    <Typography>{getDurationString(sessionContext?.session.maxDurationSec, false)}</Typography>
                                ) : (
                                    <AllInclusiveIcon fontSize="medium" />
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={4}>
                            <Stack spacing={1} direction="row">
                                <Typography textAlign="start">
                                    <b>Question:</b>
                                </Typography>
                                {sessionContext?.session.questionTimeSec ? (
                                    <Typography>{getDurationString(sessionContext?.session.questionTimeSec, false)}</Typography>
                                ) : (
                                    <AllInclusiveIcon fontSize="medium" />
                                )}
                            </Stack>
                        </Grid>
                        {quiz.quizTypeId === QuizTypes.Battleship && (
                            <>
                                <Grid item xs={4}>
                                    <Typography textAlign="start">
                                        <b>Grid size:</b>
                                        {` ${quizSetup.rows} rows, ${quizSetup.columns} columns`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography textAlign="start">
                                        <b>Ship count:</b>
                                        {` ${getShipCount()}`}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Paper>
            </Box>
            <br />
            <TableContainer component={Paper} sx={{ width: '80%' }}>
                <Table sx={{ minWidth: 650 }} aria-label="user session overview table">
                    <TableHead>
                        <TableRow>
                            <TableCell variant="head" align="center">
                                User
                            </TableCell>
                            <TableCell variant="head" align="center">
                                Correct Answers
                            </TableCell>
                            <TableCell variant="head" align="center">
                                Incorrect or Skipped
                            </TableCell>
                            {quiz.quizTypeId === QuizTypes.Battleship && (
                                <TableCell variant="head" align="center">
                                    Ships sunk
                                </TableCell>
                            )}
                            {quiz.quizTypeId === QuizTypes.Sharpshooter && (
                                <TableCell variant="head" align="center">
                                    Shoot down
                                </TableCell>
                            )}
                            <TableCell variant="head" align="center">
                                Time Started
                            </TableCell>
                            <TableCell variant="head" align="center">
                                Time of Last Answer
                            </TableCell>
                            <TableCell variant="head" align="center">
                                Total Play Time
                            </TableCell>
                            <TableCell variant="head" align="center">
                                Is Playing
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {userSessions.map((row) => (
                            <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell component="th" scope="row">
                                    {row.userDisplayName}
                                </TableCell>
                                <TableCell align="center">{row.correctAnswers}</TableCell>
                                <TableCell align="center">{row.incorrectAnswers}</TableCell>
                                {(quiz.quizTypeId === QuizTypes.Battleship || quiz.quizTypeId === QuizTypes.Sharpshooter) && (
                                    <TableCell align="center">{getSunkShipsStatus(row)}</TableCell>
                                )}
                                <TableCell align="center">{row.timeStarted}</TableCell>
                                <TableCell align="center">{row.timeLastAnswer}</TableCell>
                                <TableCell align="center">{row.playDuration}</TableCell>
                                <TableCell align="center">
                                    {row.hasEndedSession ? (
                                        <Tooltip title="Finished">
                                            <SelfImprovementIcon />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Playing">
                                            <DirectionsRunIcon />
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};
